/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import SeamlessImmutable from 'seamless-immutable';
import { pick, without, findWhere, sortBy } from 'underscore';
import { getScaledWidgetWidthsForNewTotalWidth } from 'DesignData/lib/layoutTreeDataHelper';
import { isCellType } from 'DesignData/helpers/widgets';
import { updateSmartRules } from 'smart-content-lib/utils/smartRules';
import { forEachNormalizedWidgets } from 'DesignData/lib/normalization/layouts/iterators';
import { getNormalizedLayoutData } from 'DesignData/lib/normalization/layouts';
import { createRowId } from 'DesignData/lib/normalization/layouts/identifiers';
import { arrayMultiReplace, arrayReplace, arrayRemove } from 'DesignData/lib/array';
import { stripDuplicateClasses } from 'DesignData/lib/css';
import { LayoutWidget } from 'DesignData/models/layouts';
import Widget from 'DesignData/models/widgets/Widget';
import { CriterionTypes } from 'smart-content-core/constants';
import { LEFT, RIGHT, TOP, BOTTOM } from 'DesignData/enums/OrientationTypes';
import { form } from 'DesignData/enums/SmartWidgetTypes';
import SmartProperties from 'DesignData/enums/SmartProperties';
import { LAYOUT_WIDGET_ADD_WIDGET_BELOW, LAYOUT_WIDGET_SPLIT, LAYOUT_WIDGET_DELETE, LAYOUT_WIDGET_GROUP_TYPE_TRANSFORM, LAYOUT_WIDGET_EDIT, LAYOUT_WIDGET_EDIT_PARAMS, LAYOUT_WIDGET_EDIT_LANGUAGE_OVERRIDES, LAYOUT_WIDGET_EDIT_SMART_RULES, LAYOUT_WIDGET_REMOVE_SMART_RULES, LAYOUT_WIDGET_SWAP, LAYOUT_WIDGET_CLONE, LAYOUT_WIDGET_CSS_CLASSES_STRIP_DUPLICATES, LAYOUT_WIDGET_MAKE_LOCAL_COPY_GLOBAL_GROUP, LAYOUT_MULTI_WIDGET_EDIT_CSS_CLASSES, LAYOUT_CELL_UNGROUP, UNIFIED_LAYOUT_FETCH, LAYOUT_CREATE_FROM_FILE, LAYOUT_WIDGET_ADD_SMART_RULE, LAYOUT_WIDGET_EDIT_SMART_RULE, LAYOUT_WIDGET_REMOVE_SMART_RULE, LAYOUT_WIDGET_EDIT_SMART_RULES_ORDER, LAYOUT_WIDGET_UPDATE_SMART_RULES, LAYOUT_DND_MOVE_WIDGET_WITHIN_ROW, LAYOUT_DND_MOVE_WIDGET_TO_NEW_ROW, LAYOUT_DND_MOVE_WIDGET_AND_CREATE_CELL, LAYOUT_DND_HORIZONTAL_RESIZE_WIDGETS, LAYOUT_DND_INSERT_SIDEBAR_WIDGET_IN_ROW, LAYOUT_DND_INSERT_SIDEBAR_WIDGET_AND_CREATE_CELL, LAYOUT_DND_MOVE_ROW, LAYOUT_DND_MOVE_WIDGET_AND_CREATE_ROW, LAYOUT_DND_MOVE_SIDEBAR_WIDGET_AND_CREATE_ROW, LAYOUT_REVERT, LAYOUT_DISCARD_BUFFER, LAYOUT_GROUPING_CREATE, LAYOUT_LOCALIZATION_DELETE_LANGUAGE, LAYOUT_LOCALIZATION_CHANGE_MASTER_LANGUAGE } from 'DesignData/actions/ActionTypes';
const EMPTY_LANGUAGE_OVERRIDES = SeamlessImmutable({});
/**
 * @param {uint} n
 * @return {Array.<uint, uint>}
 */

function splitEvenly(n) {
  const half = Math.ceil(n / 2);
  return [half, n - half];
}
/**
 * Return the widget id of the widget nearest the passed `widgetId` in `row`.
 *
 * @param {Array} row
 * @param {String} widgetId
 * @return {String}
 */


function getAdjacentWidgetId(row, widgetId) {
  // If widget is first in row, return the second in the row.
  if (row[0] === widgetId) {
    return row[1];
  } // Otherwise return the widget before widgetId


  const index = row.indexOf(widgetId);
  return row[index - 1];
}
/**
 * Correct all `x` values of widgets in a row.
 *
 * @param {Object} widgets  Widgets collection.
 * @param {Object} rows     Rows collection.
 * @param {String} rowId    Row to fix widgets in.
 * @return {Object.<String, Object>} Updated widgets and rows collections.
 */


function correctWidgetX(widgets, rows, rowId) {
  let currentX = 0;
  rows[rowId].forEach(widgetId => {
    const widget = widgets[widgetId];
    widgets = widgets.setIn([widgetId, 'x'], currentX);
    currentX += widget.w;
  });
  return {
    widgets,
    rows
  };
}
/**
 * Remove a widget from a row and cleanup parent cell and row as needed.
 *
 * @param {Object} widgets  Widgets collection.
 * @param {Object} rows     Rows collection.
 * @param {String} widgetId Widget id of widget removed.
 * @param {String} rowId    Row id of widget removed.
 * @param {String} parentId Widget id of of parent cell container widget removed.
 * @return {Object.<String, Object>} Updated widgets and rows collections.
 */


function removeWidgetFromSourceRowAndUpdateAdjacentWidgets(widgets, rows, widgetId, rowId, parentId) {
  // Update source row and source row widgets
  const sourceRow = rows[rowId];

  if (sourceRow.length === 1) {
    // Source widget was only widget in source row.
    // Delete row and remove row reference from parent widget's `rows` list.
    rows = rows.without(rowId);
    const parentRows = widgets[parentId].rows;
    widgets = widgets.setIn([parentId, 'rows'], without(parentRows, rowId));
  } else {
    const sourceWidget = widgets[widgetId]; // Multiple widgets in source row.
    // Find widget adjacent to widget being removed.

    const adjacentWidgetId = getAdjacentWidgetId(sourceRow, widgetId);
    const adjacentWidget = widgets[adjacentWidgetId]; // Set `w` of adjacent widget to fill space of the widget removed.

    widgets = widgets.setIn([adjacentWidgetId, 'w'], adjacentWidget.w + sourceWidget.w); // Remove widgetId from row.

    rows = rows.set(rowId, without(sourceRow, widgetId));
    ({
      widgets
    } = correctWidgetX(widgets, rows, rowId));
  }

  return {
    widgets,
    rows
  };
}
/**
 * Remove a widget from state.
 *
 * @param {Object} widgets  Widgets collection.
 * @param {Object} rows     Rows collection.
 * @param {String} widgetId Widget id of widget removed.
 * @param {String} rowId    Row id of widget removed.
 * @param {String} parentId Widget id of of parent cell container widget removed.
 * @return {Array.<Object, Object>} Updated widgets and rows collections.
 */


function deleteWidget(widgets, rows, widgetId, rowId, parentId) {
  const widget = widgets[widgetId];

  if (!widget) {
    // This can occur when both a parent and child are selected for deletion
    // and the parent is deleted first.
    return {
      widgets,
      rows
    };
  }

  const widgetsToRemove = [];
  const rowsToRemove = []; // If widget is a cell, remove all child rows and widgets from state.

  if (isCellType(widget.type)) {
    forEachNormalizedWidgets(widgets, rows, widgetId, wgt => {
      widgetsToRemove.push(wgt.id);
      rowsToRemove.push(...wgt.rows);
    });
  } // Remove widget from widgets collection.


  ({
    widgets,
    rows
  } = removeWidgetFromSourceRowAndUpdateAdjacentWidgets(widgets, rows, widgetId, rowId, parentId));

  if (widgetsToRemove.length) {
    widgets = widgets.without(widgetsToRemove);
  }

  if (rowsToRemove.length) {
    rows = rows.without(rowsToRemove);
  }

  widgets = widgets.without(widgetId);
  return {
    widgets,
    rows
  };
}
/**
 *
 * @param {Object} widgets  Widgets collection.
 * @param {String} widgetId Widget id of widget to be updated.
 * @param {String} langCode
 */


function deleteLocalization(widgets, widgetId, langCode) {
  const widget = widgets[widgetId];

  if (widget && widget.languageOverrides && widget.languageOverrides[langCode]) {
    const updatedWidget = widget.update('languageOverrides', overrides => overrides.without(langCode));
    widgets = widgets.set(widgetId, updatedWidget);
  }

  return widgets;
}
/*
 * layoutData
 *
 * Normalizes a recursive layout tree into collections of widgets and rows.
 * Also makes consistent flex nodes so their data is structured like cells while we work with them on the frontend.
 * //TODO: Normalizr? Made an initial stab with it but the depth we need to mutate didn't make this any less bulkly.
 */


export default function layoutData(state = SeamlessImmutable({
  rootWidgetId: null,
  rows: {},
  widgets: {}
}), action) {
  switch (action.type) {
    /*
     * Populates state from a GET /layouts/unified/:layoutId
     */
    case UNIFIED_LAYOUT_FETCH:
    case LAYOUT_CREATE_FROM_FILE:
    case LAYOUT_DISCARD_BUFFER:
    case LAYOUT_REVERT:
      {
        const {
          layout
        } = action;

        if (!layout) {
          return state;
        }

        const data = getNormalizedLayoutData(layout);
        return state.merge(data);
      }

    /***********************************
     *
     * Layout Tree Mutations (not Drag-n-Drop)
     *
     **********************************/

    /*
     * Halves the target widget and adds a new widget to its right.
     */

    case LAYOUT_WIDGET_SPLIT:
      {
        const {
          rowId,
          widgetId,
          newWidget
        } = action;
        const {
          rows,
          widgets
        } = state;
        const widgetToBeSplit = widgets[widgetId];
        const [wLeft, wRight] = splitEvenly(widgetToBeSplit.w);
        const updatedWidgetToBeSplit = widgetToBeSplit.merge({
          w: wLeft
        });
        const newLayoutWidget = new LayoutWidget(Object.assign({}, newWidget, {
          w: wRight,
          x: updatedWidgetToBeSplit.x + updatedWidgetToBeSplit.w
        }));
        const row = rows[rowId];
        const indexOfWidgetToBeSplit = row.indexOf(updatedWidgetToBeSplit.id);
        const newRow = row.slice(0, indexOfWidgetToBeSplit + 1).concat(newLayoutWidget.id, row.slice(indexOfWidgetToBeSplit + 1));
        const newRows = rows.set(rowId, newRow);
        const newWidgets = widgets.merge({
          [updatedWidgetToBeSplit.id]: updatedWidgetToBeSplit,
          [newLayoutWidget.id]: newLayoutWidget
        });
        return state.merge({
          rows: newRows,
          widgets: newWidgets
        });
      }

    /*
     * Delete widgets.
     */

    case LAYOUT_WIDGET_DELETE:
      {
        let {
          widgets,
          rows
        } = state;
        action.widgets.forEach(({
          widgetId,
          rowId,
          parentId
        }) => {
          ({
            widgets,
            rows
          } = deleteWidget(widgets, rows, widgetId, rowId, parentId));
        });
        return state.merge({
          widgets,
          rows
        });
      }

    /*
     * Delete language from localization
     */

    case LAYOUT_LOCALIZATION_CHANGE_MASTER_LANGUAGE:
    case LAYOUT_LOCALIZATION_DELETE_LANGUAGE:
      {
        const {
          langCode
        } = action;
        let {
          widgets
        } = state;
        Object.keys(widgets).forEach(widgetId => {
          widgets = deleteLocalization(widgets, widgetId, langCode);
        });
        return state.merge({
          widgets
        });
      }

    /*
     * Handle `type` change for cell/group widgets
     */

    case LAYOUT_WIDGET_GROUP_TYPE_TRANSFORM:
      {
        const {
          widgets,
          data
        } = action;

        if (!(widgets && widgets.length && data && data.type)) {
          return state;
        }

        widgets.forEach(({
          widgetId
        }) => {
          if (!state.widgets[widgetId]) return;
          state = SeamlessImmutable.setIn(state, ['widgets', widgetId, 'type'], data.type);
        });
        return state;
      }

    /*
     * Handle value edits to widget{}
     */

    case LAYOUT_WIDGET_EDIT:
      {
        const {
          widgetId,
          edits
        } = action;
        const widget = state.widgets[widgetId];
        return state.setIn(['widgets', widgetId], widget.merge(edits));
      }

    /*
     * Handle value edits to widget.params{}
     */

    case LAYOUT_WIDGET_EDIT_PARAMS:
      {
        const {
          widgetId,
          edits
        } = action;
        const {
          params
        } = state.widgets[widgetId];
        return state.setIn(['widgets', widgetId, 'params'], params.merge(edits));
      }

    /**
     * Handle value edits to widget.languageOverrides
     */

    case LAYOUT_WIDGET_EDIT_LANGUAGE_OVERRIDES:
      {
        const {
          widgetId,
          langCode,
          edits
        } = action;
        const {
          languageOverrides = EMPTY_LANGUAGE_OVERRIDES
        } = state.widgets[widgetId];
        return state.setIn(['widgets', widgetId, 'languageOverrides'], languageOverrides.update(langCode, overrides => {
          if (overrides) {
            return overrides.merge(edits);
          }

          return SeamlessImmutable(edits);
        }));
      }

    /*
     * Edit multiple widgets' CSS Classes
     *
     * TODO: Normalize CSS classes with their own reducer.
     *        - Edit in place.
     *        - Account for all belonging to a layout.
     *        - Intellisense.
     */

    case LAYOUT_MULTI_WIDGET_EDIT_CSS_CLASSES:
      {
        const {
          prevClassData,
          nextClassData,
          widgetIds
        } = action;
        let {
          widgets
        } = state;
        let removed = prevClassData.order.filter(className => !nextClassData.classes[className]);
        let added = nextClassData.order.filter(className => !prevClassData.classes[className]);
        const modified = []; // Find existing classes that were modified - prepended, appended, or trimmed.

        removed.forEach(removedClass => {
          added.some(addedClass => {
            if (!~addedClass.indexOf(removedClass) && !~removedClass.indexOf(addedClass)) {
              return false;
            }

            modified.push({
              old: removedClass,
              updated: addedClass
            }); // Removed from removed/added lists

            removed = arrayRemove(removed, removedClass);
            added = arrayRemove(added, addedClass);
            return true;
          });
        });
        widgetIds.forEach(widgetId => {
          let {
            order
          } = widgets[widgetId].getCssClassData();
          removed.forEach(removedClass => {
            order = arrayRemove(order, removedClass);
          });
          added.forEach(addedClass => {
            order = order.concat(addedClass);
          });
          modified.forEach(({
            old,
            updated
          }) => {
            order = arrayReplace(order, old, updated, true);
          });
          widgets = widgets.setIn([widgetId, 'params', 'css_class'], order.join(' '));
        });
        return state.set('widgets', widgets);
      }

    /*
     * Strips duplicate css classes from widget.params.css_class
     */

    case LAYOUT_WIDGET_CSS_CLASSES_STRIP_DUPLICATES:
      {
        const {
          widgetIds
        } = action;
        let {
          widgets
        } = state;
        widgetIds.forEach(widgetId => {
          const widget = widgets[widgetId];

          if (!widget) {
            // This could occur when a selected widget is deleted.
            return;
          }

          const cssClass = widget.params.css_class || '';
          const stripped = stripDuplicateClasses(cssClass);

          if (cssClass === stripped) {
            return;
          }

          widgets = widgets.setIn([widgetId, 'params', 'css_class'], stripped);
        });
        return state.set('widgets', widgets);
      }

    /*
     * Handle edits to widget's smart content
     */

    case LAYOUT_WIDGET_EDIT_SMART_RULES:
      {
        const {
          widgetId,
          smartObject
        } = action;
        const widget = state.widgets[widgetId];
        const {
          type,
          params
        } = widget;
        let smartAttrs = Object.assign({}, pick(smartObject, SmartProperties), {
          overrideable: false
        });
        let defaultSmartContent;

        if (type === form) {
          if (smartObject.content) {
            defaultSmartContent = Object.assign({}, smartObject.content);
          }
        } else {
          const targetContentType = widget.getTargetContentType();

          if (smartObject[targetContentType]) {
            defaultSmartContent = {
              [targetContentType]: smartObject[targetContentType]
            };
          }
        }

        if (defaultSmartContent) {
          smartAttrs = Object.assign({}, smartAttrs, {}, defaultSmartContent);
        }

        const smartObjects = smartObject.smart_objects;
        smartAttrs = Object.assign({}, smartAttrs, {
          smart_objects: smartObjects
        });
        const newParams = params.merge(smartAttrs);
        return state.setIn(['widgets', widgetId, 'params'], newParams);
      }

    case LAYOUT_WIDGET_REMOVE_SMART_RULES:
      {
        const {
          widgetId
        } = action;
        const params = state.widgets[widgetId].params;
        const paramsWithoutSmartContent = params.without([...SmartProperties, 'overrideable']);
        return state.setIn(['widgets', widgetId, 'params'], paramsWithoutSmartContent);
      }

    /**
     * Handle adding an individual widget smart rule
     */

    case LAYOUT_WIDGET_ADD_SMART_RULE:
      {
        const {
          widgetId,
          smartObject
        } = action;
        const {
          params: widgetParams
        } = state.widgets[widgetId];
        const smartRules = widgetParams.smart_objects || [];
        const updatedSmartRules = [...smartRules, smartObject];
        const newParams = widgetParams.merge({
          smart_objects: sortBy(updatedSmartRules, 'order')
        });
        return state.setIn(['widgets', widgetId, 'params'], newParams);
      }

    /**
     * Handle edits to individual widget smart rules
     */

    case LAYOUT_WIDGET_EDIT_SMART_RULE:
      {
        const {
          widgetId,
          smartRuleId,
          edits
        } = action;
        const {
          params: widgetParams
        } = state.widgets[widgetId];
        const smartRules = widgetParams.smart_objects || [];
        const updatedSmartRules = smartRules.map(rule => {
          if (rule.criterion_id !== smartRuleId) {
            return rule;
          }

          return Object.assign({}, rule, {}, edits, {
            body: Object.assign({}, edits)
          });
        });
        const newParams = widgetParams.merge({
          smart_objects: updatedSmartRules
        });
        return state.setIn(['widgets', widgetId, 'params'], newParams);
      }

    case LAYOUT_WIDGET_REMOVE_SMART_RULE:
      {
        const {
          widgetId,
          smartRuleId
        } = action;
        const {
          params: widgetParams
        } = state.widgets[widgetId];
        const smartRules = widgetParams.smart_objects || [];
        const updatedSmartRules = smartRules.filter(rule => rule.criterion_id !== smartRuleId);
        let newParams;

        if (updatedSmartRules.length) {
          newParams = widgetParams.merge({
            smart_objects: updatedSmartRules
          });
        } else {
          newParams = widgetParams.without([...SmartProperties, 'overrideable']);
        }

        return state.setIn(['widgets', widgetId, 'params'], newParams);
      }

    case LAYOUT_WIDGET_EDIT_SMART_RULES_ORDER:
      {
        const {
          widgetId,
          smartObjects
        } = action;
        const {
          params: widgetParams
        } = state.widgets[widgetId];
        const existingSmartRules = widgetParams.smart_objects || [];
        const existingSmartRulesById = existingSmartRules.reduce((hash, rule) => {
          hash[rule.criterion_id] = rule;
          return hash;
        }, {});
        const updatedSmartRules = smartObjects.map(smartObject => {
          const {
            criterion_id: smartRuleId
          } = smartObject;
          const existingSmartRule = existingSmartRulesById[smartRuleId];

          if (!existingSmartRule) {
            return smartObject;
          }

          return Object.assign({}, existingSmartRule, {}, smartObject);
        });
        const newParams = widgetParams.merge({
          smart_objects: sortBy(updatedSmartRules, 'order')
        });
        return state.setIn(['widgets', widgetId, 'params'], newParams);
      }

    case LAYOUT_WIDGET_UPDATE_SMART_RULES:
      {
        const {
          widgetId,
          smartObjects,
          smartMetaData
        } = action;
        const {
          definitionId,
          smartType
        } = smartMetaData;
        const {
          params: widgetParams
        } = state.widgets[widgetId];
        const {
          smart_objects
        } = widgetParams;
        const newParams = widgetParams.merge({
          definition_id: definitionId,
          smart_objects: updateSmartRules(smart_objects, smartObjects),
          smart_type: smartType
        });
        return state.setIn(['widgets', widgetId, 'params'], newParams);
      }

    /*
     * Wraps target widget in a cell and adds a new widget below it.
     */

    case LAYOUT_WIDGET_ADD_WIDGET_BELOW:
      {
        const {
          widgetId,
          rowId,
          newCell,
          newWidget
        } = action;
        let {
          widgets,
          rows
        } = state;
        const targetWidget = widgets[widgetId];
        const newLayoutWidget = new LayoutWidget(newWidget); // Create 2 new rows for the new cell

        const childRow1 = createRowId();
        const childRow2 = createRowId(); // Create cell that will replace target widget

        const newLayoutCell = new LayoutWidget(Object.assign({}, newCell, {
          x: targetWidget.x,
          w: targetWidget.w,
          rows: [childRow1, childRow2]
        })); // Replace widget in row with the new cell.

        rows = rows.merge({
          [rowId]: rows[rowId].map(wid => wid === widgetId ? newLayoutCell.id : wid),
          [childRow1]: [targetWidget.id],
          [childRow2]: [newLayoutWidget.id]
        }); // Add new widgets to widgets collection.
        // Update target widget to be full width.

        widgets = widgets.merge({
          [newLayoutCell.id]: newLayoutCell,
          [newLayoutWidget.id]: newLayoutWidget,
          [targetWidget.id]: targetWidget.merge({
            x: 0,
            w: 12
          })
        });
        return state.merge({
          rows,
          widgets
        });
      }

    /*
     * Copies/clones a widget and inserts it in the proceeding row.
     */

    case LAYOUT_WIDGET_CLONE:
      {
        const {
          parentId,
          rowId,
          cloneRowId,
          cloneWidgetId,
          cloneWidgets,
          cloneRows
        } = action;
        let {
          widgets,
          rows
        } = state; // Add clone rows/widgets to local copy of state.

        widgets = widgets.merge(cloneWidgets);
        rows = rows.merge(cloneRows); // Set clone widget to be full width.

        widgets = widgets.setIn([cloneWidgetId, 'x'], 0);
        widgets = widgets.setIn([cloneWidgetId, 'w'], 12); // Reset CSS IDs since they should be unique.

        forEachNormalizedWidgets(widgets, rows, cloneWidgetId, wgt => {
          const widgetParams = widgets[wgt.id].params;

          if (!widgetParams.css_id && !widgetParams.definition_id && !widgetParams.smart_objects && !widgetParams.smart_type) {
            return;
          }

          widgets = widgets.setIn([wgt.id, 'params', 'css_id'], '').setIn([wgt.id, 'params', 'definition_id'], null).setIn([wgt.id, 'params', 'smart_objects'], []).setIn([wgt.id, 'params', 'smart_type'], CriterionTypes.NOT_SMART);
        }); // Insert cloned widget into parent cell after the origin rowId.

        const parentCell = widgets[parentId];
        const insertIndex = parentCell.rows.indexOf(rowId) + 1;
        const newParentCell = parentCell.merge({
          rows: [...parentCell.rows.slice(0, insertIndex), cloneRowId, ...parentCell.rows.slice(insertIndex)]
        }); // Add new row to local state.

        rows = rows.merge({
          [cloneRowId]: [cloneWidgetId]
        }); // Update parent cell in local state.

        widgets = widgets.merge({
          [parentId]: newParentCell
        });
        return state.merge({
          rows,
          widgets
        });
      }

    /*
     * Swap widget for another type.
     */

    case LAYOUT_WIDGET_SWAP:
      {
        const {
          widgetId,
          newWidget
        } = action;
        const {
          x,
          w
        } = state.widgets[widgetId];
        const newLayoutWidget = new LayoutWidget(Object.assign({}, newWidget, {
          id: widgetId,
          x,
          w
        }));
        return state.setIn(['widgets', widgetId], newLayoutWidget);
      }

    /*
     * Swaps global group widget with cell of the global group's tree.
     */

    case LAYOUT_WIDGET_MAKE_LOCAL_COPY_GLOBAL_GROUP:
      {
        const {
          widgetId,
          globalGroup
        } = action;

        if (!globalGroup) {
          return state;
        }

        let {
          label
        } = globalGroup;
        label = typeof label === 'string' ? label.split('/').pop() : '';
        const originalWidget = state.widgets[widgetId];
        const originalCssParams = pick(originalWidget.params, Widget.DEFAULT_CSS_FIELDS);
        const {
          widgets,
          rows,
          rootWidgetId
        } = getNormalizedLayoutData(globalGroup, {
          nodeModifier(node) {
            // If the same group is made local multiple times, we need to ensure
            // that the ids will be unique.
            node.id = node.name = Widget.generateWidgetName();
          }

        });
        let root = widgets[rootWidgetId];
        root = root.merge({
          label,
          id: widgetId,
          name: widgetId,
          x: originalWidget.x,
          w: originalWidget.w,
          params: root.params.merge(originalCssParams)
        });
        widgets[widgetId] = root;
        delete widgets[rootWidgetId];
        return state.merge({
          widgets: state.widgets.merge(widgets),
          rows: state.rows.merge(rows)
        });
      }

    /*
     * Ungroup cell
     */

    case LAYOUT_CELL_UNGROUP:
      {
        const {
          widgetId,
          rowId
        } = action;
        let {
          widgets,
          rows
        } = state;
        const cell = widgets[widgetId];
        const cellWidth = cell.w;
        const childRowId = cell.rows[0];
        const childWidgetIds = rows[childRowId]; // Remove cell from row and add child widgets in its place.

        rows = rows.set(rowId, arrayMultiReplace(rows[rowId], [widgetId], childWidgetIds)); // Update widget widths.

        {
          const oldWidgetWidths = childWidgetIds.map(id => widgets[id].w);
          const newWidgetWidths = getScaledWidgetWidthsForNewTotalWidth(oldWidgetWidths, 12, cellWidth);
          childWidgetIds.forEach((wid, idx) => {
            const w = newWidgetWidths[idx];
            widgets = widgets.setIn([wid, 'w'], w);
          });
        } // Remove cell and its row from collections.

        rows = rows.without(childRowId);
        widgets = widgets.without(widgetId);
        ({
          widgets
        } = correctWidgetX(widgets, rows, rowId));
        return state.merge({
          rows,
          widgets
        });
      }

    /**********************************
     *
     * Grouping - via Layout Editor mode activated in toolbar.
     *
     **********************************/

    case LAYOUT_GROUPING_CREATE:
      {
        let {
          widgets,
          rows
        } = state;
        const {
          groupingMatrix,
          cell
        } = action;
        const {
          parentId,
          rows: rowMatrices
        } = groupingMatrix; // Update widget widths.

        const scaleGroupWidgetsToFillRow = group => {
          const oldWidgetWidths = group.widgets.map(id => widgets[id].w);
          const newWidgetWidths = getScaledWidgetWidthsForNewTotalWidth(oldWidgetWidths, group.w, 12);
          group.widgets.forEach((wid, idx) => {
            const w = newWidgetWidths[idx];
            widgets = widgets.setIn([wid, 'w'], w);
          });
          return widgets;
        };

        if (rowMatrices.length === 1) {
          /*
           * Only one row of selections.
           * Create group just for those selections and insert into existing row of widgets.
           */
          const {
            rowId,
            groups
          } = rowMatrices[0];
          const group = findWhere(groups, {
            isSelected: true
          }); // Create new row to be use by new cell.

          const childRowId = createRowId(); // Create new cell.

          const newLayoutCell = new LayoutWidget(Object.assign({}, cell.generateNewLayoutWidgetObject(), {
            x: group.x,
            w: group.w,
            rows: [childRowId]
          }));
          widgets = widgets.set(newLayoutCell.id, newLayoutCell); // Update state with changes to rows/widgets.

          const row = rows[rowId]; // Remove selected widgets from source row and insert group in their place.

          rows = rows.set(rowId, arrayMultiReplace(row, group.widgets, [newLayoutCell.id]));
          rows = rows.set(childRowId, [...group.widgets]);
          widgets = scaleGroupWidgetsToFillRow(group);
          ({
            widgets
          } = correctWidgetX(widgets, rows, childRowId));
        } else {
          /*
           * Multiple rows of selections.
           * All widgets, selected or not, will be grouped.
           * Old rows will be removed and a new row containing the groups will be inserted.
           */
          const newLayoutCells = [];
          const rowsToRemoveFromParent = [];
          rowMatrices.forEach(rowMatrix => {
            const {
              rowId,
              groups
            } = rowMatrix;
            rowsToRemoveFromParent.push(rowId);
            groups.forEach((group, groupIndex) => {
              let newLayoutCell = newLayoutCells[groupIndex] || new LayoutWidget(Object.assign({}, cell.generateNewLayoutWidgetObject(), {
                x: group.x,
                w: group.w,
                rows: []
              }));
              const childRowId = createRowId();
              newLayoutCell = newLayoutCell.set('rows', [...newLayoutCell.rows, childRowId]);
              newLayoutCells[groupIndex] = newLayoutCell;
              rows = rows.set(childRowId, [...group.widgets]);
              widgets = widgets.set(newLayoutCell.id, newLayoutCell);
              widgets = scaleGroupWidgetsToFillRow(group);
              ({
                widgets
              } = correctWidgetX(widgets, rows, childRowId));
            });
          }); // Create new row of grouped widgets to insert into parent widget.

          const groupedWidgetsRowId = createRowId();
          rows = rows.set(groupedWidgetsRowId, newLayoutCells.map(cellWidget => cellWidget.id));
          rows = rows.without(rowsToRemoveFromParent); // Remove old rows in parent and insert new row with groups.

          const parentRows = widgets[parentId].rows || [];
          widgets = widgets.setIn([parentId, 'rows'], arrayMultiReplace(parentRows, rowsToRemoveFromParent, [groupedWidgetsRowId]));
        }

        return state.merge({
          rows,
          widgets
        });
      }

    /**********************************
     *
     * Drag-n-Drop
     *
     **********************************/

    /*
     * Drop a widget on another widget's left/right drop target within the same row.
     * Effectively a reorder of widgets within a row.
     */

    case LAYOUT_DND_MOVE_WIDGET_WITHIN_ROW:
      {
        const {
          widgetId,
          rowId,
          targetWidgetId,
          orientation
        } = action;
        let {
          rows,
          widgets
        } = state; // Update order of row

        const row = rows[rowId];
        const rowWithout = without(row, widgetId);
        const targetWidgetIndex = rowWithout.indexOf(targetWidgetId);
        const insertIndex = orientation === LEFT ? targetWidgetIndex : targetWidgetIndex + 1;
        const reorderedRow = [...rowWithout.slice(0, insertIndex), widgetId, ...rowWithout.slice(insertIndex)];
        rows = rows.set(rowId, reorderedRow);
        ({
          widgets
        } = correctWidgetX(widgets, rows, rowId));
        return state.merge({
          rows,
          widgets
        });
      }

    /*
     * Drop a widget on another widget's left/right drop target within a different row.
     * Remove widget from old row, resize widgets in old row, insert widget in new, resize widgets in new row.
     */

    case LAYOUT_DND_MOVE_WIDGET_TO_NEW_ROW:
      {
        const {
          widgetId,
          rowId,
          parentId,
          targetWidgetId,
          targetRowId,
          orientation
        } = action;
        let {
          rows,
          widgets
        } = state; // Remove widget from former row.

        ({
          widgets,
          rows
        } = removeWidgetFromSourceRowAndUpdateAdjacentWidgets(widgets, rows, widgetId, rowId, parentId)); // Insert in new row

        {
          const targetRow = rows[targetRowId];
          const targetWidgetIndex = targetRow.indexOf(targetWidgetId);
          const insertIndex = orientation === LEFT ? targetWidgetIndex : targetWidgetIndex + 1;
          rows = rows.set(targetRowId, [...targetRow.slice(0, insertIndex), widgetId, ...targetRow.slice(insertIndex)]);
        } // Update `w` values

        {
          const targetWidget = widgets[targetWidgetId];
          const [wLeft, wRight] = splitEvenly(targetWidget.w);
          const updatedSourceWidget = widgets[widgetId].set('w', orientation === LEFT ? wLeft : wRight);
          const updatedTargetWidget = widgets[targetWidgetId].set('w', orientation === RIGHT ? wLeft : wRight);
          widgets = widgets.merge({
            [widgetId]: updatedSourceWidget,
            [targetWidgetId]: updatedTargetWidget
          });
          ({
            widgets
          } = correctWidgetX(widgets, rows, targetRowId));
        }
        return state.merge({
          rows,
          widgets
        });
      }

    /*
     * Drop a widget on another widget's top/bottom drop target to create a new module group (aka cell).
     */

    case LAYOUT_DND_MOVE_WIDGET_AND_CREATE_CELL:
      {
        const {
          widgetId,
          rowId,
          parentId,
          targetWidgetId,
          targetRowId,
          orientation,
          newCell
        } = action;
        let {
          rows,
          widgets
        } = state;
        const {
          w: sourceWidgetW
        } = widgets[widgetId];
        const {
          w: targetWidgetW,
          x: targetWidgetX
        } = widgets[targetWidgetId]; // Create 2 new rows for the new cell

        const childRow1 = createRowId();
        const childRow2 = createRowId(); // Create cell that will replace target widget

        const newLayoutCell = new LayoutWidget(Object.assign({}, newCell, {
          x: targetWidgetX,
          w: targetWidgetW,
          rows: [childRow1, childRow2]
        }));
        rows = rows.merge({
          // Replace target widget in target row with the new cell.
          [targetRowId]: rows[targetRowId].map(wid => wid === targetWidgetId ? newLayoutCell.id : wid),
          // Add source and target widgets to new cell in order determined by drop position.
          [childRow1]: [orientation === TOP ? widgetId : targetWidgetId],
          [childRow2]: [orientation === BOTTOM ? widgetId : targetWidgetId]
        }); // Add new cell to widgets collection.

        widgets = widgets.merge({
          [newLayoutCell.id]: newLayoutCell
        });
        const sourceRow = rows[rowId];

        if (rowId === targetRowId) {
          // Same Row
          // Have new cell expand to fill width of source widget.
          widgets = widgets.setIn([newLayoutCell.id, 'w'], newLayoutCell.w + sourceWidgetW); // Remove widgetId from row.

          rows = rows.set(rowId, without(sourceRow, widgetId));
          ({
            widgets
          } = correctWidgetX(widgets, rows, rowId));
        } else {
          ({
            widgets,
            rows
          } = removeWidgetFromSourceRowAndUpdateAdjacentWidgets(widgets, rows, widgetId, rowId, parentId));
        } // Update widget `w` values in new cell to be full width.


        widgets = widgets.setIn([widgetId, 'x'], 0).setIn([widgetId, 'w'], 12).setIn([targetWidgetId, 'x'], 0).setIn([targetWidgetId, 'w'], 12);
        return state.merge({
          rows,
          widgets
        });
      }

    /*
     * Drop a sidebar widget on a widget's left/right drop target to insert the new widget.
     */

    case LAYOUT_DND_INSERT_SIDEBAR_WIDGET_IN_ROW:
      {
        const {
          widget,
          targetWidgetId,
          targetRowId,
          orientation
        } = action;
        let {
          rows,
          widgets
        } = state;
        const targetWidget = widgets[targetWidgetId];
        const [wLeft, wRight] = splitEvenly(targetWidget.w); // Create new widget for insertion.

        const newLayoutWidget = new LayoutWidget(Object.assign({}, widget, {
          w: orientation === LEFT ? wLeft : wRight
        }));
        widgets = widgets.setIn([targetWidgetId, 'w'], orientation === LEFT ? wRight : wLeft);
        widgets = widgets.set(newLayoutWidget.id, newLayoutWidget); // Insert new widget in row.

        const targetRow = rows[targetRowId];
        const targetWidgetIndex = targetRow.indexOf(targetWidgetId);
        const insertIndex = orientation === LEFT ? targetWidgetIndex : targetWidgetIndex + 1;
        rows = rows.set(targetRowId, [...targetRow.slice(0, insertIndex), newLayoutWidget.id, ...targetRow.slice(insertIndex)]);
        ({
          widgets
        } = correctWidgetX(widgets, rows, targetRowId));
        return state.merge({
          rows,
          widgets
        });
      }

    /*
     * Drop a widget on another widget's top/bottom drop target to create a new module group (aka cell) and insert the new widget.
     */

    case LAYOUT_DND_INSERT_SIDEBAR_WIDGET_AND_CREATE_CELL:
      {
        const {
          widget,
          targetWidgetId,
          targetRowId,
          orientation,
          newCell
        } = action;
        let {
          rows,
          widgets
        } = state;
        const targetWidget = widgets[targetWidgetId];
        const newLayoutWidget = new LayoutWidget(Object.assign({}, widget, {
          x: 0,
          w: 12
        })); // Create 2 new rows for the new cell

        const childRow1 = createRowId();
        const childRow2 = createRowId(); // Create cell that will replace target widget

        const newLayoutCell = new LayoutWidget(Object.assign({}, newCell, {
          x: targetWidget.x,
          w: targetWidget.w,
          rows: [childRow1, childRow2]
        }));
        rows = rows.merge({
          // Replace target widget in target row with the new cell.
          [targetRowId]: rows[targetRowId].map(wid => wid === targetWidgetId ? newLayoutCell.id : wid),
          // Add source and target widgets to new cell in order determined by drop position.
          [childRow1]: [orientation === TOP ? newLayoutWidget.id : targetWidgetId],
          [childRow2]: [orientation === BOTTOM ? newLayoutWidget.id : targetWidgetId]
        });
        widgets = widgets.merge({
          // Add new cell and widget to widgets collection.
          [newLayoutCell.id]: newLayoutCell,
          [newLayoutWidget.id]: newLayoutWidget,
          // Set target widget (now in cell) to be full width.
          [targetWidgetId]: targetWidget.merge({
            x: 0,
            w: 12
          })
        });
        return state.merge({
          rows,
          widgets
        });
      }

    /*
     * Drop a row on a row target.
     */

    case LAYOUT_DND_MOVE_ROW:
      {
        const {
          sourceParentId,
          sourceRowId,
          targetParentId,
          targetRowId
        } = action;
        let {
          widgets
        } = state; // Remove source row from source parent.

        const sourceRows = widgets[sourceParentId].rows;
        widgets = widgets.setIn([sourceParentId, 'rows'], arrayMultiReplace(sourceRows, [sourceRowId], []));
        {
          // Insert source row in target parent.
          const targetRows = widgets[targetParentId].rows;
          const index = targetRowId ? targetRows.indexOf(targetRowId) : targetRows.length;
          widgets = widgets.setIn([targetParentId, 'rows'], [...targetRows.slice(0, index), sourceRowId, ...targetRows.slice(index)]);
        }
        return state.merge({
          widgets
        });
      }

    /*
     * Drop a widget on a row target.
     */

    case LAYOUT_DND_MOVE_WIDGET_AND_CREATE_ROW:
      {
        const {
          sourceParentId,
          sourceRowId,
          sourceWidgetId,
          targetParentId,
          targetRowId
        } = action;

        if (sourceRowId === targetRowId) {
          return state;
        }

        let {
          rows,
          widgets
        } = state; // Remove widget from source row.

        ({
          widgets,
          rows
        } = removeWidgetFromSourceRowAndUpdateAdjacentWidgets(widgets, rows, sourceWidgetId, sourceRowId, sourceParentId)); // Set widget to be full width.

        const sourceWidget = widgets[sourceWidgetId];
        widgets = widgets.merge({
          [sourceWidgetId]: sourceWidget.merge({
            x: 0,
            w: 12
          })
        }); // Create new row with widget.

        const newRowId = createRowId();
        rows = rows.set(newRowId, [sourceWidgetId]); // Add row to new parent cell.

        const targetRows = widgets[targetParentId].rows;
        const index = targetRowId ? targetRows.indexOf(targetRowId) : targetRows.length;
        widgets = widgets.setIn([targetParentId, 'rows'], [...targetRows.slice(0, index), newRowId, ...targetRows.slice(index)]);
        return state.merge({
          rows,
          widgets
        });
      }

    /*
     * Drop a sidebar widget on a row target.
     */

    case LAYOUT_DND_MOVE_SIDEBAR_WIDGET_AND_CREATE_ROW:
      {
        const {
          widget,
          targetParentId,
          targetRowId
        } = action;
        let {
          rows,
          widgets
        } = state; // Create new widget for insertion.

        const newLayoutWidget = new LayoutWidget(Object.assign({}, widget, {
          x: 0,
          w: 12
        }));
        widgets = widgets.set(newLayoutWidget.id, newLayoutWidget); // Create new row with new widget.

        const newRowId = createRowId();
        rows = rows.set(newRowId, [newLayoutWidget.id]); // Add row to new parent cell.

        const targetRows = widgets[targetParentId].rows;
        const index = targetRowId ? targetRows.indexOf(targetRowId) : targetRows.length;
        widgets = widgets.setIn([targetParentId, 'rows'], [...targetRows.slice(0, index), newRowId, ...targetRows.slice(index)]);
        return state.merge({
          rows,
          widgets
        });
      }

    /*
     * Horizontally resize adjoining widgets.
     */

    case LAYOUT_DND_HORIZONTAL_RESIZE_WIDGETS:
      {
        const {
          leftWidgetId,
          rightWidgetId,
          orientation,
          increment
        } = action;
        const leftWidget = state.widgets[leftWidgetId];
        const rightWidget = state.widgets[rightWidgetId];
        const widgetWidthBeingDecreased = orientation === LEFT ? leftWidget.w : rightWidget.w;

        if (widgetWidthBeingDecreased === 1) {
          return state;
        }

        const allowableResizeIncrement = widgetWidthBeingDecreased > increment ? // Ok
        increment : // Requested increment is beyound boundary - use largest increment possible.
        widgetWidthBeingDecreased - (widgetWidthBeingDecreased - 1);
        let newLeftW;
        let newRightW;

        if (orientation === LEFT) {
          newLeftW = leftWidget.w - allowableResizeIncrement;
          newRightW = rightWidget.w + allowableResizeIncrement;
        } else {
          newLeftW = leftWidget.w + allowableResizeIncrement;
          newRightW = rightWidget.w - allowableResizeIncrement;
        }

        const {
          x: oldRightX,
          w: oldRightW
        } = rightWidget;
        const newRightX = oldRightX + (oldRightW - newRightW);
        return state.setIn(['widgets', leftWidgetId, 'w'], newLeftW).setIn(['widgets', rightWidgetId, 'w'], newRightW).setIn(['widgets', rightWidgetId, 'x'], newRightX);
      }

    default:
      return state;
  }
}